// WAAP Analytics → API — NATIVE charts (no Superset iframe). The BE pre-
// normalizes Superset dashboard 60 ("waap.API Analytics") into typed widgets
// (number / table / timeseries). This board has NO Domain filter — only range.
//   GET /analytics/api/charts?tz=…&range=…
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';

/** Time-range options for API analytics — the dashboard's "time_range" TEMPLATE
 *  values (matched verbatim in the dataset SQL). */
export const API_TIME_RANGES = [
  'Last Hour',
  'Last 24 Hours',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
] as const;
export type ApiTimeRange = (typeof API_TIME_RANGES)[number];

/** Read the normalized API-analytics widgets for a website's account. Returns
 *  null when the board is not configured server-side (400/404/501). */
export function useWaapApiCharts(domainId: string | undefined, range = 'Last 24 Hours') {
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.apiAnalyticsCharts), range] as const,
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, range });
        const path = waapPath(domainId as string, WAAP_AREAS.apiAnalyticsCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(`${path}?${params.toString()}`);
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
